import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PredictionSection } from '../components/stream/predictions';
import StreamInfo from '../components/stream/StreamInfo';
import ChatSection from '../components/stream/ChatSection';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import useWebSocketChat from '../hooks/useWebSocketChat';
import {
  ShareIcon,
  HeartIcon,
  EyeIcon,
  UserPlusIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

const Live = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);

  // Core state
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false); // Loading state for prediction actions
  const [predictionResult, setPredictionResult] = useState(null); // Stores result after prediction ends
  const [showPredictionForm, setShowPredictionForm] = useState(false); // Controls visibility of the creation form
  // Removed unused wasMuted state

  // Chat state
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);

  // Use our WebSocket chat hook
  const {
    messages,
    error: chatError,
    sendMessage: chatSendMessage,
    isConnected: chatConnected,
    socketRef: chatSocketRef,
    isUserTyping,
    setIsUserTyping
  } = useWebSocketChat(id, user);

  // Share functionality
  const shareOptions = [
    {
      name: 'Copy Link',
      icon: ClipboardDocumentIcon,
      action: () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success('Stream URL copied to clipboard!');
        setShowShareModal(false);
      },
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      name: 'Twitter',
      icon: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      ),
      action: () => {
        const text = `Check out this amazing stream: ${stream?.title || 'Live Stream'}`;
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
        window.open(url, '_blank');
        setShowShareModal(false);
      },
      color: 'bg-sky-500 hover:bg-sky-600'
    },
    {
      name: 'Discord',
      icon: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
      ),
      action: () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success('Stream URL copied! Share it in Discord.');
        setShowShareModal(false);
      },
      color: 'bg-indigo-500 hover:bg-indigo-600'
    },
    {
      name: 'Telegram',
      icon: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      ),
      action: () => {
        const text = `Check out this amazing stream: ${stream?.title || 'Live Stream'}`;
        const url = `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
        setShowShareModal(false);
      },
      color: 'bg-cyan-500 hover:bg-cyan-600'
    }
  ];

  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // Stream interaction state
  const [streamState, setStreamState] = useState({
    liked: false,
    isFollowing: false,
    likeCount: 0,
    followersCount: 0,
    viewerCount: 0,
    isStreamCreator: false,
    likeLoading: false,
    followLoading: false,
    endingStream: false,
    playbackError: null
  });

  // Refs
  const twitchPlayerRef = useRef(null); // For Twitch embed
  const streamCheckInterval = useRef(null);
  const currentStreamId = useRef(id);

  // Debug helper
  const logStreamInfo = useCallback((prefix, data) => {
    console.log(`${prefix}:`, {
      id: currentStreamId.current,
      status: data?.status,
      streamUrl: data?.streamUrl,
      isLive: data?.isLive,
    });
  }, []);

  // Fetch stream
  const fetchStream = useCallback(async () => {
    if (!currentStreamId.current?.match(/^[0-9a-fA-F]{24}$/)) {
      setError('Invalid stream ID');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.streams.details(currentStreamId.current)}`, {
        headers: user?.isConnected ? {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        } : {}
      });

      if (!response.ok) {
        throw new Error('Stream not found');
      }

      const data = await response.json();
      logStreamInfo('Fetched stream', data);

      // Only set stream if it's still the same ID
      if (currentStreamId.current === id) {
        const isFollowing = data.followers?.includes(user?.address) || false;

        // **** Refined Check ****
        // Explicitly check if user and user.address exist before comparing
        let isCreator = false;
        if (user && user.address) {
            // Convert both addresses to lowercase for case-insensitive comparison
            const creatorAddress = data.creator?.address?.toLowerCase();
            const userAddress = user.address.toLowerCase();
            isCreator = creatorAddress === userAddress;

            // Enhanced logging with lowercase comparison
            console.log('[Live.jsx] Creator Check:', {
                apiCreatorAddress: data.creator?.address,
                userAddress: user.address,
                apiCreatorAddressLower: creatorAddress,
                userAddressLower: userAddress,
                isCreatorResult: isCreator
            });
        } else {
             console.log('[Live.jsx] Creator Check skipped: User or user.address missing.', { userExists: !!user, addressExists: !!user?.address });
        }
        // **** End Refined Check ****

        setStream(data);
        setStreamState(prev => ({
          ...prev,
          // Use the calculated isCreator value
          isStreamCreator: isCreator,
          liked: data.likes?.includes(user?.address) || false,
          likeCount: data.likes?.length || 0,
          isFollowing,
          followersCount: data.followers?.length || 0,
          viewerCount: data.viewers || 0
        }));

        if (data.status === 'scheduled') {
          const scheduleDate = new Date(data.scheduledStartTime);
          toast(`Stream scheduled for ${scheduleDate.toLocaleDateString()} at ${scheduleDate.toLocaleTimeString()}`);
        }
      }

    } catch (err) {
      console.error('Error fetching stream:', err);
      setError(err.message);
      toast.error(err.message);

      // Retry logic for recoverable errors
      if (retryCount < maxRetries && err.message !== 'Invalid stream ID') {
        setRetryCount(prev => prev + 1);
        setTimeout(fetchStream, 2000 * (retryCount + 1));
      }
    } finally {
      setLoading(false);
    }
  }, [id, user, retryCount, logStreamInfo]);

  // We no longer need the setupHlsPlayer function since we only support Twitch streams

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (streamCheckInterval.current) {
        clearInterval(streamCheckInterval.current);
      }
      if (window.typingTimeoutRef) {
        clearTimeout(window.typingTimeoutRef);
        window.typingTimeoutRef = null;
      }
      currentStreamId.current = null;
    };
  }, []);

  // Function to fetch predictions for the current stream
  const fetchPredictions = useCallback(async () => {
    if (!stream?.streamId) return;

    // Don't set loading state if we already have a prediction
    // This prevents flickering when refreshing predictions
    if (!prediction) {
      setPredictionLoading(true);
    }

    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`Fetching predictions for stream ${stream.streamId}`);
    }

    try {
      // Try the direct route - this is the most reliable
      const response = await fetch(`${API_BASE_URL}/api/simple-predictions/${stream.streamId}`);

      if (response.ok) {
        // Success - we have a prediction
        const data = await response.json();
        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log('Prediction data received');
        }
        setPrediction(data);
      } else if (response.status === 404) {
        // 404 is expected when there are no predictions
        // This is normal for new streams or after predictions are cleared
        setPrediction(null);

        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log('No active predictions found for this stream - this is normal');
        }
      } else {
        // Other error status codes should be logged
        console.warn(`Unexpected status code ${response.status} when fetching predictions`);
        // Don't clear existing prediction on unexpected errors
        // This prevents flickering when there's a temporary API issue

        try {
          const errorData = await response.json();
          console.log('Error details:', errorData);
        } catch (e) {
          // Ignore JSON parsing errors
        }
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
      // Don't clear existing prediction on error
      // This prevents flickering when there's a temporary network issue
    } finally {
      setPredictionLoading(false);
    }
  }, [stream?.streamId, prediction]);

  // Initial stream fetch and ID update
  useEffect(() => {
    setLoading(true);
    setRetryCount(0);
    currentStreamId.current = id;
    fetchStream();
  }, [id, fetchStream]);

  // No special setup needed for Twitch streams
  // The iframe is created in the render function

  // Regular status check for live streams
  useEffect(() => {
    if (stream?.status === 'live') {
      streamCheckInterval.current = setInterval(fetchStream, 30000);
      return () => clearInterval(streamCheckInterval.current);
    }
  }, [stream?.status, fetchStream]);

  // Fetch predictions when stream data is loaded
  useEffect(() => {
    if (stream) {
      // Initial fetch - only if the form is not open
      if (!showPredictionForm) {
        fetchPredictions();
      }

      // Set up interval to periodically check for new predictions
      // Use a longer interval (60 seconds) to reduce server load
      // This is fine since we also get updates via WebSocket
      const predictionInterval = setInterval(() => {
        // Only fetch if the stream is still live, we don't already have a prediction,
        // and the prediction form is not open
        if (stream.status === 'live' && !prediction && !showPredictionForm) {
          console.log('Periodic prediction check (60s interval)');
          fetchPredictions();
        } else if (showPredictionForm) {
          console.log('Skipping periodic prediction check while form is open');
        }
      }, 60000);

      return () => clearInterval(predictionInterval);
    }
  }, [stream, fetchPredictions, prediction, showPredictionForm]);

  // WebSocket connection setup for predictions
  useEffect(() => {
    // Listen for prediction-related messages from the chat WebSocket
    const handlePredictionMessages = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'prediction_update') {
          console.log('Received prediction update:', data.prediction);

          // Don't update prediction state if the form is currently being shown
          // This prevents the form from being reset while the user is filling it out
          if (showPredictionForm) {
            console.log('Ignoring prediction update while form is open');
            return;
          }

          if (data.prediction) {
            if (data.prediction.status === 'active') {
              setPrediction(data.prediction);
              setPredictionResult(null);
            } else if (data.prediction.status === 'ended') {
              setPredictionResult(data.prediction);
              setPrediction(null);
            }
          } else {
            setPrediction(null);
            setPredictionResult(null);
          }
        } else if (data.type === 'prediction_resolved') {
          console.log('Received prediction resolved:', data.prediction);

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction resolved while form is open');
            return;
          }

          setPredictionResult(data.prediction);
          setPrediction(null);
        } else if (data.type === 'prediction_ended') {
          console.log('Received prediction_ended notification');

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction_ended while form is open');
            return;
          }

          setPrediction(null);
        } else if (data.type === 'prediction_deleted') {
          console.log('Received prediction_deleted notification');

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction_deleted while form is open');
            return;
          }

          setPrediction(null);
          setPredictionResult(null);

          if (streamState.isStreamCreator && !showPredictionForm) {
            setShowPredictionForm(true);
          }
        } else if (data.type === 'prediction_cleared') {
          console.log('Received prediction_cleared notification:', data);

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction_cleared while form is open');
            return;
          }

          setPredictionResult(null);

          if (data.isManualClear) {
            if (!streamState.isStreamCreator) {
              toast.info('The stream creator has cleared the prediction');
            }

            if (streamState.isStreamCreator && !showPredictionForm) {
              setShowPredictionForm(true);
            }
          } else {
            if (streamState.isStreamCreator) {
              toast.success('Prediction cleared. You can now create a new prediction!');
            }
          }
        } else if (data.type === 'viewer_count') {
          setStreamState(prev => ({ ...prev, viewerCount: data.count }));
        }
      } catch (err) {
        console.error('Error processing prediction WebSocket message:', err);
      }
    };

    // Add event listener to the WebSocket
    if (chatSocketRef?.current) {
      chatSocketRef.current.addEventListener('message', handlePredictionMessages);
    }

    // Also set up polling as a fallback, but don't poll when the form is open
    const predictionPollInterval = setInterval(() => {
      // Only poll if the stream exists AND the prediction form is NOT being shown
      if (stream?.streamId && !showPredictionForm) {
        console.log('Polling for predictions (form closed)');
        fetchPredictions();
      } else if (showPredictionForm) {
        console.log('Skipping prediction poll while form is open');
      }
    }, 10000); // Poll every 10 seconds as a fallback

    return () => {
      // Clean up event listeners
      if (chatSocketRef?.current) {
        chatSocketRef.current.removeEventListener('message', handlePredictionMessages);
      }
      clearInterval(predictionPollInterval);
    };
  }, [chatSocketRef, stream?.streamId, streamState.isStreamCreator, showPredictionForm, fetchPredictions]);



  // Typing timeout ref to avoid using window
  const typingTimeoutRef = useRef(null);

  // Optimized message input handler
  const handleMessageInputChange = useCallback((value) => {
    // Update the message input
    setMessageInput(value);

    // Mark that the user is typing when they change the input
    if (value.length > 0) {
      setIsUserTyping(true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to mark as not typing
      typingTimeoutRef.current = setTimeout(() => {
        setIsUserTyping(false);
      }, 2000);
    } else {
      // If input is empty, immediately mark as not typing
      setIsUserTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  }, [setIsUserTyping]);

  // Chat message handler
  const handleSendMessage = useCallback((e) => {
    if (e.type === 'click' || (e.type === 'keypress' && e.key === 'Enter')) {
      e.preventDefault();

      if (!user?.isConnected) {
        toast.error('Please login to chat');
        return;
      }

      if (!messageInput.trim()) return;

      try {
        // Clear typing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Mark that the user is done typing
        setIsUserTyping(false);

        // Use our chatSendMessage function from the useWebSocketChat hook
        const messageContent = messageInput.trim();
        const success = chatSendMessage(messageContent);

        if (success) {
          // Clear the input field
          setMessageInput('');
          console.log('Message sent successfully');
        }
      } catch (err) {
        console.error('Error sending message:', err);
        toast.error('Failed to send message. Please try again.');
      }
    }
  }, [messageInput, user, chatSendMessage, setIsUserTyping]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Log prediction state changes
  useEffect(() => {
    console.log('[Live.jsx] Prediction State Update:', {
      prediction,
      predictionResult,
      isStreamCreator: streamState.isStreamCreator,
      user: user?.address
    });

    // Note: We've removed the automatic timer for clearing predictions
    // Now we're relying on the stream creator to manually clear predictions
    // using the "Clear Prediction & Create New" button
  }, [prediction, predictionResult, streamState.isStreamCreator, user?.address]);

  // --- Prediction Handlers ---

  // Create a prediction
  const handleCreatePrediction = async (formData) => {
    if (!user?.token) {
      toast.error("Authentication required to create prediction.");
      return false; // Return false to indicate failure
    }
    if (!stream?.streamId) {
      toast.error("Stream ID not found.");
      return false; // Return false to indicate failure
    }

    console.log('Creating prediction with data:', formData);
    setPredictionLoading(true);

    // Debug the incoming formData
    console.log('[Debug] formData received in handleCreatePrediction:', {
      formData,
      incentiveType: typeof formData.incentiveType,
      incentiveAmount: typeof formData.incentiveAmount,
      incentiveAmountValue: formData.incentiveAmount
    });

    // Validate incentive amount if provided
    let validatedIncentiveAmount;
    if (formData.incentiveType && formData.incentiveAmount !== undefined && formData.incentiveAmount !== null) {
      // Ensure incentiveAmount is a valid number
      if (typeof formData.incentiveAmount === 'string') {
        validatedIncentiveAmount = parseFloat(formData.incentiveAmount);
      } else if (typeof formData.incentiveAmount === 'number') {
        validatedIncentiveAmount = formData.incentiveAmount;
      } else {
        console.error('[Debug] Invalid incentiveAmount type:', typeof formData.incentiveAmount);
        toast.error(`Invalid amount: ${formData.incentiveAmount} is not a valid number`);
        setPredictionLoading(false);
        return false;
      }

      if (isNaN(validatedIncentiveAmount) || validatedIncentiveAmount <= 0) {
        console.error('[Debug] Invalid parsed incentiveAmount:', validatedIncentiveAmount);
        toast.error(`Invalid amount: ${formData.incentiveAmount} is not a valid number`);
        setPredictionLoading(false);
        return false;
      }
    }

    // Prepare data: convert duration from minutes to seconds
    const payload = {
      ...formData,
      duration: formData.duration * 60, // Convert minutes to seconds
      incentiveAmount: validatedIncentiveAmount || undefined, // Use validated amount
      incentiveType: formData.incentiveType || undefined, // Send undefined if empty
    };

    // Debug the payload before sending to API
    console.log('[Debug] Prediction payload:', {
      payload,
      incentiveType: payload.incentiveType,
      incentiveAmount: payload.incentiveAmount
    });

    try {
      // First, try to create a test prediction to check if the database is working
      let dbWorking = false;
      try {
        const dbCheckResponse = await fetch(`${API_BASE_URL}/api/debug/db-status`);
        if (dbCheckResponse.ok) {
          const dbStatus = await dbCheckResponse.json();
          dbWorking = dbStatus.connected;
          console.log('Database status check:', dbStatus);
          if (!dbWorking) {
            toast.error('Database connection issue detected. Please try again later.');
            return false; // Return false to indicate failure
          }
        }
      } catch (dbError) {
        console.warn('Could not check database status:', dbError);
        // Continue anyway, as this is just a precaution
      }

      // Now create the actual prediction
      const response = await fetch(`${API_BASE_URL}/api/streams/${stream.streamId}/predictions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}` // Use token from context
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create prediction' }));
        // Check for specific error messages from backend
        if (errorData.error === 'An active prediction already exists') {
          toast.error('Cannot create: An active prediction already exists for this stream.');
        } else if (errorData.error?.includes('Insufficient')) {
           toast.error(errorData.error); // Show specific balance error
        } else {
          throw new Error(errorData.error || 'Failed to create prediction');
        }
        // Don't proceed if response not ok
        setPredictionLoading(false);
        return false; // Return false to indicate failure
      }

      const newPrediction = await response.json();
      console.log('New prediction created successfully:', newPrediction);
      setPrediction(newPrediction); // Update local state with the new prediction
      setShowPredictionForm(false); // Hide form after successful creation
      toast.success('Prediction created successfully!');

      // Manually fetch predictions after a short delay to ensure the new one is included
      setTimeout(() => {
        fetchPredictions();
      }, 1000);

      return true; // Return true to indicate success

    } catch (error) {
      console.error("Error creating prediction:", error);
      toast.error(error.message || "Failed to create prediction");
      return false; // Return false to indicate failure
    } finally {
      setPredictionLoading(false);
    }
  };


  // Vote on a prediction - returns a Promise for the PredictionSection component
  const handleVotePrediction = async (option) => {
    if (!user?.token) {
      toast.error("Authentication required to vote on prediction.");
      return Promise.reject(new Error("Authentication required"));
    }
    if (!prediction?.streamId || !prediction?._id) {
      toast.error("Prediction information is missing.");
      return Promise.reject(new Error("Prediction information missing"));
    }

    console.log(`Voting for ${option} on prediction ${prediction._id}`);
    setPredictionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/streams/${prediction.streamId}/predictions/${prediction._id}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ option })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to vote on prediction' }));
        throw new Error(errorData.error || 'Failed to vote on prediction');
      }

      const updatedPrediction = await response.json();
      setPrediction(updatedPrediction);
      toast.success('Vote recorded successfully!');
      return Promise.resolve(updatedPrediction); // Return a resolved promise with the updated prediction
    } catch (error) {
      console.error("Error voting on prediction:", error);
      toast.error(error.message || "Failed to vote on prediction");
      setPredictionLoading(false);
      return Promise.reject(error); // Return a rejected promise with the error
    } finally {
      setPredictionLoading(false);
    }
  };

  // End a prediction and declare a winner
  const handleEndPrediction = async (winnerOptionKey) => {
    if (!user?.token) {
      toast.error("Authentication required to end prediction.");
      return;
    }
    if (!prediction?.streamId || !prediction?._id) {
      toast.error("Prediction information is missing.");
      return;
    }
    if (!streamState.isStreamCreator) {
      toast.error("Only the stream creator can end predictions.");
      return;
    }

    console.log(`Ending prediction ${prediction._id}, winner: ${winnerOptionKey}`);
    setPredictionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/streams/${prediction.streamId}/predictions/${prediction._id}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ winner: winnerOptionKey })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to end prediction' }));
        throw new Error(errorData.error || 'Failed to end prediction');
      }

      const result = await response.json();
      setPredictionResult(result);
      setPrediction(null); // Clear the active prediction

      // Manually send a WebSocket message to ensure all clients are notified
      if (chatSocketRef?.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({
          type: 'prediction_resolved',
          streamId: prediction.streamId,
          prediction: result,
          winningOption: winnerOptionKey
        }));
        console.log('Sent prediction_resolved WebSocket message');
      }

      // Inform the user that they can create a new prediction after 1 minute
      toast.success(`Prediction ended! ${result.option1} vs ${result.option2} - Winner: ${result[winnerOptionKey]}`);
      toast.info('You can create a new prediction in 1 minute', { duration: 5000 });
    } catch (error) {
      console.error("Error ending prediction:", error);
      toast.error(error.message || "Failed to end prediction");
    } finally {
      setPredictionLoading(false);
    }
  };

  // --- End Prediction Handlers ---

  // Function to manually clear prediction result for all users
  const handleClearPredictionResult = useCallback(async () => {
    console.log('Stream creator manually clearing prediction result');

    if (!user?.token) {
      toast.error("Authentication required to clear prediction.");
      return;
    }

    if (!predictionResult?._id) {
      console.warn('No prediction result to clear');
      return;
    }

    try {
      // First clear locally
      setPredictionResult(null);

      // Show loading toast
      const loadingToast = toast.loading('Deleting prediction...');

      // Call the API to delete the prediction
      const response = await fetch(`${API_BASE_URL}/api/streams/${id}/predictions/${predictionResult._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        }
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete prediction' }));
        throw new Error(errorData.error || 'Failed to delete prediction');
      }

      // Show success message
      toast.success('Prediction deleted. You can now create a new prediction!');

      // Show the prediction form after a short delay
      setTimeout(() => {
        setShowPredictionForm(true);
      }, 500);

      // Also send a WebSocket message to ensure all clients are notified immediately
      if (chatSocketRef?.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({
          type: 'prediction_deleted',
          streamId: id,
          predictionId: predictionResult._id,
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error deleting prediction:', error);
      toast.error(error.message || 'Failed to delete prediction');

      // Restore the prediction result if deletion failed
      setPredictionResult(predictionResult);
    }
  }, [id, user, predictionResult]);

  // Handle ending a stream
  const handleEndStream = async () => {
    if (!user?.token) {
      toast.error("Authentication required to end stream.");
      return;
    }
    if (!stream?.streamId) {
      toast.error("Stream information is missing.");
      return;
    }
    if (!streamState.isStreamCreator) {
      toast.error("Only the stream creator can end the stream.");
      return;
    }

    // Set loading state
    setStreamState(prev => ({ ...prev, endingStream: true }));

    try {
      console.log(`Ending stream ${stream.streamId}`);

      const response = await fetch(`${API_BASE_URL}/api/streams/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          streamId: stream.streamId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to end stream' }));
        throw new Error(errorData.error || 'Failed to end stream');
      }

      // Parse response but we don't need to use it
      await response.json();

      // Notify via WebSocket that the stream has ended
      if (chatSocketRef?.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({
          type: 'stream_ended',
          streamId: stream.streamId
        }));
        console.log('Sent stream_ended WebSocket message');
      }

      // Update the stream state
      setStream(prev => ({ ...prev, status: 'ended', isLive: false }));

      toast.success('Stream ended successfully!');

      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error("Error ending stream:", error);
      toast.error(error.message || "Failed to end stream");
    } finally {
      setStreamState(prev => ({ ...prev, endingStream: false }));
    }
  };

  // No need to clear messages on unmount as they're managed by the useChatFallback hook

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-t-2 border-b-2 border-purple-500 rounded-full"
        />
        <div className="ml-4 text-white">Loading stream...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 bg-red-500/10 p-6 rounded-lg border border-red-500"
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
            <span className="text-2xl text-red-500">!</span>
          </div>
          <h2 className="text-xl font-bold text-white">Stream Error</h2>
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => {
              setLoading(true);
              setError(null);
              setRetryCount(0);
              fetchStream();
            }}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </motion.div>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-bold text-white">Stream Not Found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/10 to-gray-900">
      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Share Stream</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {shareOptions.map((option, index) => (
                  <motion.button
                    key={option.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={option.action}
                    className={`${option.color} text-white p-4 rounded-xl transition-all duration-200 transform hover:scale-105 flex flex-col items-center gap-2`}
                  >
                    {typeof option.icon === 'function' ? (
                      <option.icon />
                    ) : (
                      <option.icon className="w-5 h-5" />
                    )}
                    <span className="text-sm font-medium">{option.name}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-12 gap-6 p-6 text-white">
        <div className="col-span-12 lg:col-span-9 space-y-6">
          {/* Player Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative group"
          >
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 shadow-2xl">
              <div className="relative aspect-video min-h-[480px]">
                {stream.streamUrl ? (
                  <iframe
                    ref={twitchPlayerRef}
                    src={`https://player.twitch.tv/?channel=${stream.streamUrl.split('/').pop()}&parent=${window.location.hostname}&muted=true`}
                    height="100%"
                    width="100%"
                    allowFullScreen={true}
                    className="absolute top-0 left-0 w-full h-full border-0 rounded-2xl"
                    title="Twitch Player"
                  ></iframe>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center space-y-4">
                      <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto">
                        <PlayIcon className="w-10 h-10 text-gray-400" />
                      </div>
                      <p className="text-gray-400 text-lg">No stream URL provided</p>
                    </div>
                  </div>
                )}

                {/* Live indicator */}
                {stream.status === 'live' && (
                  <div className="absolute top-4 left-4 z-10">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      LIVE
                    </div>
                  </div>
                )}

                {/* Stream actions overlay */}
                <div className="absolute top-4 right-4 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="bg-black/50 backdrop-blur-sm text-white p-2 rounded-full hover:bg-black/70 transition-all duration-200 transform hover:scale-110"
                    title="Share stream"
                  >
                    <ShareIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Stream Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StreamInfo
              stream={stream}
              user={user}
              handleShare={() => setShowShareModal(true)}
              handleEndStream={handleEndStream}
              {...streamState}
            />
          </motion.div>

          {/* Enhanced Stream Creator Controls */}
          {streamState.isStreamCreator && predictionResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 backdrop-blur-sm rounded-2xl p-6 border border-green-700/50"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Creator Controls</h3>
                    <p className="text-green-400 text-sm">Manage your stream predictions</p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleClearPredictionResult}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center gap-3 font-medium shadow-lg hover:shadow-green-500/25"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
                Clear Prediction & Create New
              </button>
            </motion.div>
          )}

          {/* Enhanced Prediction Section */}
          {stream && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <PredictionSection
                // Data props
                prediction={prediction}
                predictionLoading={predictionLoading}
                predictionResult={predictionResult}
                streamId={stream.streamId}
                isStreamCreator={streamState.isStreamCreator}
                user={user}
                // State control props
                showPredictionForm={showPredictionForm}
                onShowForm={() => setShowPredictionForm(true)}
                onHideForm={() => setShowPredictionForm(false)}
                // Action handlers
                onCreatePrediction={handleCreatePrediction}
                onVotePrediction={handleVotePrediction}
                onEndPrediction={handleEndPrediction}
                onClearPredictionResult={handleClearPredictionResult}
              />
            </motion.div>
          )}
        </div>

        {/* Enhanced Chat Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="col-span-12 lg:col-span-3"
        >
          <ChatSection
            messages={messages}
            chatError={chatError}
            messageInput={messageInput}
            setMessageInput={handleMessageInputChange}
            handleSendMessage={handleSendMessage}
            messagesEndRef={messagesEndRef}
            user={user}
            streamId={id}
            isConnected={chatConnected}
            socketRef={chatSocketRef}
            isUserTyping={isUserTyping}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default Live;
